<!-- SubscriberModal.vue -->
<template>
  <v-dialog v-model="internalOpen" max-width="640" persistent scrollable>
    <v-card rounded="lg">
      <v-card-title class="modal-header pa-0">
        <div class="d-flex align-center pa-5">
          <span class="head-sub-text">
            <v-icon class="mr-2">mdi-account</v-icon>
            {{ isEdit ? "Edit" : "Add" }} Subscriber
          </span>
          <v-spacer />
          <v-btn icon variant="text" size="small" @click="close()">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
      </v-card-title>

      <v-divider />

      <v-card-text class="pa-6">
        <v-form ref="subForm" v-model="isValid">
          <p class="field-label">Email address</p>
          <v-text-field
            class="mb-3"
            ref="email"
            :rules="rules.email"
            v-model="formData.email"
            density="compact"
            variant="outlined"
            hide-details="auto"
          />

          <p class="field-label">First Name</p>
          <v-text-field
            class="mb-3"
            ref="fname"
            :rules="rules.required"
            v-model="formData.first_name"
            density="compact"
            variant="outlined"
            hide-details="auto"
          />

          <p class="field-label">Last Name</p>
          <v-text-field
            class="mb-3"
            ref="lname"
            :rules="rules.required"
            v-model="formData.last_name"
            density="compact"
            variant="outlined"
            hide-details="auto"
          />

          <p class="field-label">List(s)</p>
          <v-autocomplete
            v-model="selectedGroup"
            :items="lists.length > 0 ? lists : []"
            color="blue-grey-lighten-2"
            item-title="name"
            item-value="id"
            label="Select a list"
            chips
            closable-chips
            density="compact"
            variant="outlined"
            hide-details="auto"
          ></v-autocomplete>
        </v-form>
      </v-card-text>

      <v-divider />

      <v-card-actions class="pa-4">
        <v-btn variant="outlined" color="#8a8a9a" class="text-none" @click="close()">
          Cancel
        </v-btn>
        <v-spacer />
        <v-btn
          color="success"
          variant="flat"
          class="text-none"
          :disabled="loadingState"
          @click="isEdit ? edit() : save()"
        >
          <span v-if="!loadingState">
            <v-icon size="18" class="mr-1">{{ isEdit ? "mdi-pencil" : "mdi-content-save" }}</v-icon>
            {{ isEdit ? "Save changes" : "Save subscriber" }}
          </span>
          <v-progress-circular :width="3" size="20" color="white" indeterminate v-else></v-progress-circular>
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import eventBus from "@/utils/eventBus";

export default {
  name: "SubscriberModal",

  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    // Pass a subscriber id to open in edit mode; omit/null for add mode
    subscriberId: {
      type: [Number, String],
      default: null,
    },
  },

  emits: ["update:modelValue", "saved"],

  data() {
    return {
      isValid: false,
      loadingState: false,
      test: [],
      selectedGroup: null,
      oldValue: false,
      formData: {
        email: "",
        first_name: "",
        last_name: "",
        groups: [],
      },
      rules: {
        required: [(value) => !!value || "Required."],
        email: [
          (value) => !!value || "Required.",
          (value) => /.+@.+\..+/.test(value) || "Invalid email address.",
        ],
      },
    };
  },

  computed: {
    internalOpen: {
      get() {
        return this.modelValue;
      },
      set(val) {
        this.$emit("update:modelValue", val);
      },
    },
    isEdit() {
      return this.subscriberId !== null && this.subscriberId !== undefined && this.subscriberId !== "";
    },
    lists() {
      return this.$store.getters["subscribers/subscribersGetters"]("Lists");
    },
    loaderState() {
      return this.$store.getters["subscribers/subscribersGetters"]("loaderState");
    },
    singleSub() {
      return this.$store.getters["subscribers/subscribersGetters"]("singleSub");
    },
  },

  watch: {
    // Reset/populate the form each time the modal opens
    modelValue: {
      handler(isOpen) {
        if (isOpen) {
          this.$store.dispatch("subscribers/getLists");
          if (this.isEdit) {
            this.$store.dispatch("subscribers/getSingleSub", parseInt(this.subscriberId));
          } else {
            this.resetForm();
          }
        } else {
          this.resetForm();
          this.$refs.subForm?.resetValidation();
        }
      },
      immediate: true,
    },

    loaderState: {
      handler: function (newValue, oldValue) {
        this.oldValue = oldValue;
        this.loadingState = newValue;
      },
      immediate: true,
    },

    singleSub: {
      handler: function () {
        if (this.isEdit && this.singleSub !== undefined && this.singleSub) {
          this.formData = { ...this.singleSub };
          this.selectedGroup = this.singleSub.groups?.[0] ?? null;
        }
      },
    },

    selectedGroup: {
      handler: function () {
        if (this.selectedGroup !== null) {
          this.test = [this.selectedGroup];
          this.formData.groups = [...this.test];
        }
      },
    },
  },

  mounted() {
    eventBus.on("subAdded", () => {
      this.$emit("saved");
      this.close();
    });
  },

  methods: {
    resetForm() {
      this.formData = {
        email: "",
        first_name: "",
        last_name: "",
        groups: [],
      };
      this.selectedGroup = null;
      this.test = [];
    },

    close() {
      this.internalOpen = false;
    },

    save() {
      if (!this.isValid) {
        this.$refs.subForm.validate();
        return;
      }
      if (this.selectedGroup === null) {
        eventBus.emit("showToast", { type: "error", message: "Please Select List" });
        return;
      }
      this.$store.dispatch("subscribers/saveSubscriber", this.formData);
    },

    edit() {
      if (!this.isValid) {
        this.$refs.subForm.validate();
        return;
      }
      if (this.selectedGroup === null) {
        eventBus.emit("showToast", { type: "error", message: "Please Select List" });
        return;
      }
      this.$store.dispatch("subscribers/editSubscriber", {
        id: parseInt(this.subscriberId),
        alldata: { ...this.formData },
      });
    },
  },
};
</script>

<style scoped>
.modal-header {
  background: #433876;
}

.head-sub-text {
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  display: flex;
  align-items: center;
}

.field-label {
  font-size: 12px;
  font-weight: 600;
  color: #6b6b80;
  margin-bottom: 4px;
}
</style>